import { Heart, Infinity as InfinityIcon, Timer, type LucideIcon } from "lucide-react";
import circleFamily from "../../assets/landing/circle-family.webp";
import avatarGrandparents from "../../assets/landing/avatar-grandparents.webp";
import avatarSitter from "../../assets/landing/avatar-sitter.webp";
import avatarTeacher from "../../assets/landing/avatar-teacher.webp";
import avatarCoach from "../../assets/landing/avatar-coach.webp";
import avatarAunt from "../../assets/landing/avatar-aunt.webp";
import avatarNurse from "../../assets/landing/avatar-nurse.webp";
import { ACCENTS, type AccentName } from "../../lib/accents";
import { cn } from "../../lib/cn";
import { Scene } from "./stage/Scene";
import { SplitWords } from "./stage/Split";
import * as fx from "./stage/fx";
import type { Timeline } from "./stage/choreography";

interface CircleNode {
  name: string;
  avatar: string;
  /** The sections this person's link shares. */
  scopes: readonly string[];
  note?: { label: string; icon: LucideIcon };
  accent: AccentName;
  /** Avatar centre, in % of the orbit square. Array order is the left → right sweep. */
  x: number;
  y: number;
  /** Labels sit outside the ring: above the top nodes, below the rest. */
  above?: boolean;
}

const NODES: readonly CircleNode[] = [
  {
    name: "Grandparents",
    avatar: avatarGrandparents,
    scopes: ["Everything"],
    note: { label: "No expiry", icon: InfinityIcon },
    accent: "violet",
    x: 6,
    y: 50,
  },
  {
    name: "Babysitter",
    avatar: avatarSitter,
    scopes: ["Allergies", "Routines", "Contacts"],
    note: { label: "Expires in 24h", icon: Timer },
    accent: "indigo",
    x: 22,
    y: 12,
    above: true,
  },
  { name: "Daycare", avatar: avatarTeacher, scopes: ["Routines", "Allergies", "Feeding"], accent: "sky", x: 24, y: 88 },
  { name: "Coach", avatar: avatarCoach, scopes: ["Contacts", "Allergies"], accent: "emerald", x: 76, y: 12, above: true },
  { name: "Aunt & uncle", avatar: avatarAunt, scopes: ["Routines", "Feeding", "Meds"], accent: "amber", x: 76, y: 88 },
  { name: "School nurse", avatar: avatarNurse, scopes: ["Medical", "Meds", "Allergies"], accent: "rose", x: 94, y: 50 },
];

/** Spoke colours, written out in full so Tailwind's JIT can see them. */
const SPOKE: Record<AccentName, string> = {
  sky: "stroke-sky-300",
  rose: "stroke-rose-300",
  amber: "stroke-amber-300",
  emerald: "stroke-emerald-300",
  violet: "stroke-violet-300",
  indigo: "stroke-indigo-300",
};

/* Orbit geometry, in % of the square (the SVG viewBox is 0 0 100 100). */
const HUB = 34;
const AVATAR = 14;
const RING = 45.5;
const RING_MASK = "circle-orbit-reveal";

const round = (n: number) => Math.round(n * 100) / 100;

/** A spoke from the hub's rim to the node's rim, so all of it draws across open space. */
function spoke(x: number, y: number) {
  const dx = x - 50;
  const dy = y - 50;
  const len = Math.hypot(dx, dy);
  const from = HUB / 2 + 2.5;
  const to = len - AVATAR / 2 - 1.5;
  return {
    x1: round(50 + (dx / len) * from),
    y1: round(50 + (dy / len) * from),
    x2: round(50 + (dx / len) * to),
    y2: round(50 + (dy / len) * to),
  };
}

/*
 * Signature: "Orbit draw". The headline's words blur-slide in, then the family
 * pops at the centre and the dashed orbit opens from the left, over the top and
 * under the bottom at once. The circle then fills node by node in a left →
 * right sweep: the spoke draws out, the avatar pops, its scopes slide out.
 */
const TIMELINE: Timeline = [
  fx.slide("[data-fx=eyebrow]", { x: -20, blur: 6, duration: 0.4 }),
  fx.slide("[data-fx=title] [data-word]", { x: -24, blur: 8, stagger: 0.05, at: "-0.25" }),
  fx.fade("[data-fx=sub]", { at: "-0.3" }),
  fx.pop("[data-fx=hub]", { from: 0.55, at: "-0.35" }),
  fx.pop("[data-fx=hub-chip]", { at: "-0.4" }),
  // Both halves of the one ring at once, so it opens left → right.
  fx.draw("[data-fx=orbit]", { duration: 1, stagger: 0, at: "-0.4" }),
  ...NODES.flatMap((node, i) => [
    fx.draw(`[data-fx=spoke-${i}]`, { duration: 0.24, at: i === 0 ? "-0.88" : "-0.34" }),
    fx.pop(`[data-fx=avatar-${i}]`, { duration: 0.38, at: "-0.08" }),
    fx.slide(`[data-fx=label-${i}]`, { x: 0, y: node.above ? 10 : -10, blur: 4, duration: 0.3, at: "-0.3" }),
  ]),
];

export function FamilyCircle() {
  return (
    <Scene
      id="circle"
      label="Your circle"
      timeline={TIMELINE}
      className="bg-gradient-to-b from-rose-50 via-white to-amber-50/40"
    >
      {/* Backdrop: blush and butter fields and a whisper of grain. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-40 top-1/4 h-[28rem] w-[28rem] rounded-full bg-rose-200/40 blur-3xl" />
        <div className="absolute -right-24 -top-32 h-[30rem] w-[30rem] rounded-full bg-amber-100/70 blur-3xl" />
        <div className="absolute -bottom-24 right-1/4 h-80 w-80 rounded-full bg-violet-200/30 blur-3xl" />
        <div className="bg-grain absolute inset-0 opacity-[0.05] mix-blend-multiply" />
      </div>

      <div className="mx-auto grid w-full max-w-6xl flex-1 content-center items-center gap-10 px-6 pb-10 pt-20 lg:grid-cols-[2fr_3fr]">
        {/* Copy */}
        <div className="relative z-10">
          <p data-fx="eyebrow" className="text-sm font-semibold uppercase tracking-wide text-rose-700">
            Your circle of care
          </p>
          <h2
            data-fx="title"
            className="mt-3 text-balance font-display text-3xl font-bold leading-[1.1] tracking-tight text-neutral-900 sm:text-4xl lg:text-5xl"
          >
            <SplitWords text="A private link for everyone in your circle." />
          </h2>
          <p data-fx="sub" className="mt-4 max-w-xl text-[15px] text-neutral-600 sm:mt-5 sm:text-lg">
            Grandparents get everything. The sitter gets bedtime and allergies. The coach gets emergency
            contacts. Every link is named, scoped to the sections you pick, and yours to switch off.
          </p>
        </div>

        <Orbit />
      </div>
    </Scene>
  );
}

/**
 * The family at the centre, its circle of caregivers on a slowly turning ring.
 * The padding reserves room for the labels hanging outside the square (above
 * the top nodes, below the bottom ones), so the grid centres the whole picture.
 */
function Orbit() {
  return (
    <div className="mx-auto w-full max-w-[20rem] sm:max-w-[27rem] sm:pb-10 sm:pt-[3.75rem] lg:max-w-[min(33rem,calc(100dvh-14.25rem))]">
      <div className="relative aspect-square w-full">
        <RingAndSpokes />
        <Hub />
        <ul className="absolute inset-0 z-20">
          {NODES.map((node, i) => (
            <Node key={node.name} node={node} index={i} />
          ))}
        </ul>
      </div>
    </div>
  );
}

function RingAndSpokes() {
  return (
    <svg viewBox="0 0 100 100" fill="none" aria-hidden="true" className="absolute inset-0 h-full w-full overflow-visible">
      <defs>
        {/* A dashed stroke can't draw itself (its dashes ARE the stroke pattern), so a solid stroke draws a mask over it. */}
        <mask id={RING_MASK} maskUnits="userSpaceOnUse" x="-10" y="-10" width="120" height="120">
          <path data-fx="orbit" d={`M${50 - RING} 50A${RING} ${RING} 0 0 1 ${50 + RING} 50`} stroke="#fff" strokeWidth={3} />
          <path data-fx="orbit" d={`M${50 - RING} 50A${RING} ${RING} 0 0 0 ${50 + RING} 50`} stroke="#fff" strokeWidth={3} />
        </mask>
      </defs>
      <g mask={`url(#${RING_MASK})`}>
        <g className="origin-center animate-spin-slow [transform-box:fill-box]">
          <circle
            cx={50}
            cy={50}
            r={RING}
            className="stroke-rose-300"
            strokeWidth={0.45}
            strokeDasharray="1.6 2.2"
            strokeLinecap="round"
          />
        </g>
      </g>
      {NODES.map((node, i) => (
        <line
          key={node.name}
          data-fx={`spoke-${i}`}
          {...spoke(node.x, node.y)}
          className={SPOKE[node.accent]}
          strokeWidth={0.5}
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

/** The family, with a soft heartbeat ring and its name chip. */
function Hub() {
  return (
    <div className="absolute left-1/2 top-1/2 z-10 w-[34%] -translate-x-1/2 -translate-y-1/2">
      <div data-fx="hub" className="relative aspect-square">
        <span
          aria-hidden="true"
          className="absolute inset-[12%] animate-ping rounded-full bg-rose-300/30"
          style={{ animationDuration: "3.2s" }}
        />
        <div className="relative h-full w-full overflow-hidden rounded-full border-4 border-white bg-gradient-to-br from-rose-100 to-amber-100 shadow-glow ring-1 ring-rose-200/70">
          <img
            src={circleFamily}
            alt="Your family"
            width={440}
            height={440}
            loading="lazy"
            draggable={false}
            className="h-full w-full scale-[1.12] select-none object-cover"
          />
        </div>
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 flex translate-y-1/3 justify-center">
          <span
            data-fx="hub-chip"
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-rose-700 shadow-soft ring-1 ring-rose-100 sm:text-xs"
          >
            <Heart className="h-3 w-3 fill-rose-500 text-rose-500" />
            Your family
          </span>
        </div>
      </div>
    </div>
  );
}

/** One caregiver: an avatar on the ring and the link it holds (scopes, expiry). */
function Node({ node, index }: { node: CircleNode; index: number }) {
  const accent = ACCENTS[node.accent];
  const NoteIcon = node.note?.icon;
  return (
    <li
      className="absolute w-[14%] -translate-x-1/2 -translate-y-1/2"
      style={{ left: `${node.x}%`, top: `${node.y}%` }}
    >
      <span className="sr-only">
        {`${node.name}: ${node.scopes.join(", ")}.${node.note ? ` ${node.note.label}.` : ""}`}
      </span>
      <div aria-hidden="true">
        <div
          data-fx={`avatar-${index}`}
          className={cn(
            "relative z-10 aspect-square rounded-full bg-white p-[6%] shadow-soft ring-[3px] sm:ring-4",
            accent.ring,
          )}
        >
          <img
            src={node.avatar}
            alt=""
            width={224}
            height={224}
            loading="lazy"
            draggable={false}
            className={cn("h-full w-full select-none rounded-full object-cover", accent.softBg)}
          />
        </div>
        <div
          className={cn(
            "absolute left-1/2 -translate-x-1/2",
            node.above ? "bottom-full mb-1.5 sm:mb-2" : "top-full mt-1.5 sm:mt-2",
          )}
        >
          {/* Name nearest the avatar; on phones the name is all there's room for. */}
          <div
            data-fx={`label-${index}`}
            className={cn(
              "flex w-max items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 shadow-soft ring-1 ring-black/5 sm:rounded-2xl sm:px-2.5 sm:py-1.5",
              node.above ? "flex-col-reverse" : "flex-col",
            )}
          >
            <p className="whitespace-nowrap text-[11px] font-semibold text-neutral-800 sm:text-sm">{node.name}</p>
            <div className="hidden gap-1 sm:flex">
              {node.scopes.map((scope) => (
                <span
                  key={scope}
                  className={cn(
                    "rounded-full border px-1.5 text-[10.5px] font-medium leading-[1.125rem]",
                    accent.border,
                    accent.softBg,
                    accent.text,
                  )}
                >
                  {scope}
                </span>
              ))}
            </div>
            {node.note && NoteIcon && (
              <p className="hidden items-center gap-1 whitespace-nowrap text-[10.5px] font-medium text-neutral-500 sm:flex">
                <NoteIcon className="h-3 w-3" />
                {node.note.label}
              </p>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
